package com.neurobs.ccabserver.translation.states

import com.neurobs.ccab.db.{PhraseData, PhraseGroupData, ProductVersionData, TranslationEngineData}
import com.neurobs.ccab.translation.OrderedTranslationPhraseData
import com.neurobs.ccabserver.translation.util.{PhraseGroupManager, PhraseTranslationManager}
import com.neurobs.js.ccab.TranslationServer
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{BooleanAttribute, GenericState, GenericStateGenerator, RegisteredChangeAttribute, ServerDataAttribute, StateAttribute}
import com.neurobs.util.SecondsTimestamp
import org.scalajs.dom.console
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks._
import slinky.core.facade.ReactElement

case class PhraseGroupSelectionState(
                                      translatorState: TranslatorState,
                                      phraseGroupsState: ServerDataAttribute[List[PhraseGroupManager]],
                                      selectedPhraseGroupState: StateAttribute[Option[PhraseGroupManager]],
                                      filterOnTranslationState: BooleanAttribute,
                                      registeredChangeState: RegisteredChangeAttribute
                               )  extends GenericState {

  def registerChange() = registeredChangeState.register()

  def reset() = {
    phraseGroupsState.reset()
    selectedPhraseGroupState.setValue(None)
  }

  def phraseGroups = phraseGroupsState.value
  def setPhraseGroups(phraseGroups: List[PhraseGroupData], translationEngines: List[TranslationEngineData]) = {
    val managers = phraseGroups.map(new PhraseGroupManager(_, translationEngines))
    phraseGroupsState.setData(managers)
  }
  def clearPhraseGroups() = phraseGroupsState.reset()

  def selectedPhraseGroup = selectedPhraseGroupState.value
  def setSelectedPhraseGroup(phraseGroup: PhraseGroupManager) = {
    setOptionState(selectedPhraseGroupState, phraseGroup)
    registerChange()
  }

  def awaitingServerPhraseGroups = phraseGroupsState.loading

  def filterOnTranslationStatus = filterOnTranslationState.value
  def toggleFilterStatus() = filterOnTranslationState.toggle()

  def loadPhraseGroups(translationEngines: List[TranslationEngineData]) = {
    TranslationServer.getProductVersions(1)(ServerResponseUtil.handleObjectResultResponse[Seq[ProductVersionData]](
      handleObject = versions => {
        val latestVersion = versions.sortBy(_.version).reverse.head
        TranslationServer.getPhraseGroups(latestVersion.id)(handleServerPhraseGroups(translationEngines))
      }
    ))

  }

  def phraseGroupById(phraseGroupId: Long) = phraseGroups match {
    case Left(_) => None
    case Right(phraseGroupManagers) =>
      phraseGroupManagers.find(_.phraseGroupId == phraseGroupId)
  }

  def handleServerPhraseGroups(translationEngines: List[TranslationEngineData]) = ServerResponseUtil.handleObjectResultResponse[Seq[PhraseGroupData]](
    handleObject = data => setPhraseGroups(data.toList, translationEngines),
    onAnyError = phraseGroupsState.setError(_)
  )

}

object PhraseGroupSelectionStateGenerator extends GenericStateGenerator[PhraseGroupSelectionState] {
  override def generateEmptyDefaultState(): PhraseGroupSelectionState = {
    PhraseGroupSelectionState(
      TranslatorStateGenerator.generateEmptyDefaultState(),
      emptyServerDataState[List[PhraseGroupManager]](),
      emptyOptionDefaultState[PhraseGroupManager](),
      emptyBooleanState(),
      emptyRegisteredChangeState()
    )
  }
}

@react
object PhraseGroupSelectionStateProvider {
  case class Props(children: ReactElement*)

  val component = FunctionalComponent[Props] { props =>

    val translatorState = TranslatorStateGenerator.accessState
    val engineSelectionState = EngineSelectionStateGenerator.accessState

    val phraseGroupsState = ServerDataAttribute(useState[Either[Option[String], List[PhraseGroupManager]]](Left(None)))
    val selectedPhraseGroupState = StateAttribute(useState[Option[PhraseGroupManager]](None))
    val filterOnTranslationState = BooleanAttribute(useState(false))
    val registeredChangeState = RegisteredChangeAttribute(useState(0))

    val phraseGroupSelectionState = PhraseGroupSelectionState(
      translatorState = translatorState,
      phraseGroupsState = phraseGroupsState,
      selectedPhraseGroupState = selectedPhraseGroupState,
      filterOnTranslationState = filterOnTranslationState,
      registeredChangeState = registeredChangeState
    )

//    useEffect(() => {
//      phraseGroupSelectionState.loadPhraseGroups(1)
//    }, Vector())

    useEffect(() => {
      phraseGroupSelectionState.reset()
      engineSelectionState.serverEnginesState.value match {
        case Left(_) =>
        case Right(translationEngines) =>
          phraseGroupSelectionState.loadPhraseGroups(translationEngines)
      }
    }, Vector(translatorState.selectedTranslator, engineSelectionState.serverEnginesState))

    PhraseGroupSelectionStateGenerator.Provider(phraseGroupSelectionState)(
      props.children:_*
    )
  }
}


